// This module owns Site Health API requests. It receives the shell-injected fetcher and returns typed response values without managing React or query state.

import type { TrackerFetcher } from '@/provider/TrackerProvider.types'
import { apiFetch } from './api'
import type {
  SiteHealthAuditRequest,
  SiteHealthAuditStatusResponse,
  SiteHealthAuditSubmission,
  SiteHealthAuditsResponse,
  SiteHealthFindingTypesResponse,
  SiteHealthInspectorResponse,
  SiteHealthOverviewResponse,
  SiteHealthPagesResponse,
  SiteHealthSnapshotResponse,
} from './siteHealth.types'

const REPORT_PAGE_LIMIT = 50
const AUDIT_LIST_LIMIT = 25

type SiteHealthReportKind = 'pages' | 'finding-types'

function requireResponse<T>(response: T | null, name: string): T {
  if (response === null) {
    throw new Error(`${name} request returned an empty body`)
  }

  return response
}

function reportPath(
  auditId: string,
  report: SiteHealthReportKind,
  cursor: string | null
): string {
  const search = new URLSearchParams({ limit: String(REPORT_PAGE_LIMIT) })

  if (cursor) search.set('cursor', cursor)

  return `/api/site-health/audits/${auditId}/${report}?${search.toString()}`
}

export async function submitSiteHealthAudit(
  fetcher: TrackerFetcher,
  request: SiteHealthAuditRequest
): Promise<SiteHealthAuditSubmission> {
  const response = await apiFetch<SiteHealthAuditSubmission>(
    fetcher,
    '/api/site-health/audits',
    {
      method: 'POST',
      body: request,
    }
  )

  return requireResponse(response, 'site health audit')
}

export async function fetchSiteHealthAudits(
  fetcher: TrackerFetcher,
  signal?: AbortSignal
): Promise<SiteHealthAuditsResponse> {
  const response = await apiFetch<SiteHealthAuditsResponse>(
    fetcher,
    `/api/site-health/audits?limit=${AUDIT_LIST_LIMIT}`,
    { signal }
  )

  return requireResponse(response, 'site health audits')
}

export async function fetchSiteHealthAuditStatus(
  fetcher: TrackerFetcher,
  auditId: string,
  signal?: AbortSignal
): Promise<SiteHealthAuditStatusResponse> {
  const response = await apiFetch<SiteHealthAuditStatusResponse>(
    fetcher,
    `/api/site-health/audits/${auditId}/status`,
    { signal }
  )

  return requireResponse(response, 'site health status')
}

export async function fetchSiteHealthOverview(
  fetcher: TrackerFetcher,
  auditId: string,
  signal?: AbortSignal
): Promise<SiteHealthOverviewResponse> {
  const response = await apiFetch<SiteHealthOverviewResponse>(
    fetcher,
    `/api/site-health/audits/${auditId}/overview`,
    { signal }
  )

  return requireResponse(response, 'site health overview')
}

export async function fetchSiteHealthPages(
  fetcher: TrackerFetcher,
  auditId: string,
  cursor: string | null,
  signal?: AbortSignal
): Promise<SiteHealthPagesResponse> {
  const response = await apiFetch<SiteHealthPagesResponse>(
    fetcher,
    reportPath(auditId, 'pages', cursor),
    { signal }
  )

  return requireResponse(response, 'site health pages')
}

export async function fetchSiteHealthFindingTypes(
  fetcher: TrackerFetcher,
  auditId: string,
  cursor: string | null,
  signal?: AbortSignal
): Promise<SiteHealthFindingTypesResponse> {
  const response = await apiFetch<SiteHealthFindingTypesResponse>(
    fetcher,
    reportPath(auditId, 'finding-types', cursor),
    { signal }
  )

  return requireResponse(response, 'site health finding types')
}

export async function fetchSiteHealthPageInspector(
  fetcher: TrackerFetcher,
  auditId: string,
  pageId: string,
  signal?: AbortSignal
): Promise<SiteHealthInspectorResponse> {
  const response = await apiFetch<SiteHealthInspectorResponse>(
    fetcher,
    `/api/site-health/audits/${auditId}/pages/${pageId}/inspector`,
    { signal }
  )

  return requireResponse(response, 'site health page inspector')
}

export async function fetchSiteHealthPageSnapshot(
  fetcher: TrackerFetcher,
  auditId: string,
  pageId: string,
  signal?: AbortSignal
): Promise<SiteHealthSnapshotResponse> {
  const response = await apiFetch<SiteHealthSnapshotResponse>(
    fetcher,
    `/api/site-health/audits/${auditId}/pages/${pageId}/snapshot`,
    { signal }
  )

  return requireResponse(response, 'site health page snapshot')
}
